import { Component, HostListener, OnInit } from '@angular/core';
import { EnergyDataService } from '../../_shared/services/energy-data.service';
import { Globals } from '../../_shared/services/globals';
import { MonitoringService } from '../../_shared/services/monitoring.service';

interface ProfileData {
    date: string;
    value: string;
}

interface InputSeries {
    data: number[];
    color: string;
    title: string;
}

interface ZoomData {
    depth: number,
    year: number,
    month: number,
    day: number,
    label: string
}

@Component({
    selector: 'my-energy',
    templateUrl: './my-energy.component.html',
    styleUrls: ['./my-energy.component.css']
})
export class MyEnergyComponent implements OnInit {
    monthData = ["januari", "februari", "maart", "april", "mei", "juni", "juli", "augustus", "september", "oktober", "november", "december"];

    // basis for calculation and Chart series
    energyData: number[] = [];
    solarData: number[] = [];
    gasData: number[] = [];

    energySerieData: InputSeries[] = [];
    gasSerieData: InputSeries[] = [];

    energyBalance = 0.00;
    energyReturn = 0.00;
    energyUsage = 0.00;

    activeAggregation = "year";
    activeYear = "";
    activeMonth = "";
    activeDay = -1;
    currentDate: Date = new Date();

    activeZoom: ZoomData = { depth: 0, year: this.currentDate.getFullYear(), month: -1, day: -1, label: "" };

    constructor(
        public globals: Globals,
        private energyDataService: EnergyDataService,
        monitoringService: MonitoringService) {
        monitoringService.logPageView();
    }

    ngOnInit() {
        this.energyDataService.dataLoadedEvent.subscribe((dataset: string) => this.refreshData());
        this.refreshData();
    }

    // Aggregation
    aggregationToYear(jsonData: ProfileData[]): number[] {
        var result: number[] = new Array(12);
        for (var i = 0; i < 12; i++) {
            result[i] = 0;
        }

        for (var d of jsonData) {
            var date = new Date(Date.parse(d.date));
            if (date.getMonth() < this.currentDate.getMonth() || (date.getMonth() == this.currentDate.getMonth() && date.getDate() <= this.currentDate.getDate())) {
                result[date.getMonth()] += this.globals.dataInEur ? parseFloat(d.value) * this.globals.energyPrice : parseFloat(d.value);
            }
        }

        return result;
    }

    aggregationToMonth(jsonData: ProfileData[], targetMonth: number): number[] {
        var result: number[] = new Array(new Date(2017, targetMonth + 1, 0).getDate()), dataFound: boolean = false;
        for (var i = 0; i < new Date(2017, targetMonth + 1, 0).getDate(); i++) {
            result[i] = 0;
        }

        for (var d of jsonData) {
            var date = new Date(Date.parse(d.date));
            if (date.getMonth() == targetMonth && (targetMonth < this.currentDate.getMonth() || targetMonth == this.currentDate.getMonth() && date.getDate() < this.currentDate.getDate() || targetMonth == this.currentDate.getMonth() && date.getDate() == this.currentDate.getDate() && date.getHours() < this.currentDate.getHours())) {
                result[date.getDate() - 1] += this.globals.dataInEur ? parseFloat(d.value) * this.globals.energyPrice : parseFloat(d.value);
                dataFound = true;
            } else if (dataFound) {
                break;
            }
        }

        return result;
    }

    aggregationToDay(jsonData: ProfileData[], targetMonth: number, targetDay: number): number[] {
        var result: number[] = new Array(24), dataFound: boolean = false;
        for (var i = 0; i < 24; i++) {
            result[i] = 0;
        }

        for (var d of jsonData) {
            var date = new Date(Date.parse(d.date));
            if (date.getMonth() == targetMonth && date.getDate() == targetDay && (targetMonth < this.currentDate.getMonth() || targetMonth == this.currentDate.getMonth() && date.getDate() < this.currentDate.getDate() || targetMonth == this.currentDate.getMonth() && date.getDate() == this.currentDate.getDate() && date.getHours() < this.currentDate.getHours())) {
                result[date.getHours()] += this.globals.dataInEur ? parseFloat(d.value) * this.globals.energyPrice : parseFloat(d.value);
                dataFound = true;
            } else if (dataFound) {
                break;
            }
        }

        return result;
    }

    getSeriesTotal(series: number[]): number {
        var result: number = 0;
        for (var i = 0; i < series.length; i++) {
            result += series[i];
        }

        return result;
    }

    // Event handler for the barClick event in the chart.
    zoomGraph(zoomObject: ZoomData) {
        this.activeZoom = zoomObject;

        let energySource = this.energyDataService.energyProfileData;
        let solarSource = this.energyDataService.solarProfileData;
        let gasSource = this.energyDataService.gasProfileData;
        if (!this.globals.demoData) {
            energySource = this.energyDataService.energyActualData;
            solarSource = this.energyDataService.solarActualData;
            gasSource = this.energyDataService.gasActualData;
        }

        if (!energySource || !solarSource || !gasSource) {
            return;
        }

        let monthId = zoomObject.month;
        let dayNumber = zoomObject.day;
        switch (zoomObject.depth) {
            case 0: // Year
                this.energyData = this.aggregationToYear(energySource);
                this.solarData = this.aggregationToYear(solarSource);
                this.gasData = this.aggregationToYear(gasSource);

                this.activeAggregation = "year";
                this.activeYear = this.currentDate.getFullYear().toString();
                this.activeMonth = "";
                this.activeDay = -1;
                this.updateSeries();
                break;
            case 1: // Month
                if (monthId != -1) {
                    // Valid month
                    this.energyData = this.aggregationToMonth(energySource, monthId);
                    this.solarData = this.aggregationToMonth(solarSource, monthId);
                    this.gasData = this.aggregationToMonth(gasSource, monthId);

                    this.activeAggregation = "month";
                    this.activeMonth = zoomObject.label;
                    this.updateSeries();
                }
                break;
            case 2: // Day
                if (monthId != -1 && dayNumber != -1) {
                    this.energyData = this.aggregationToDay(energySource, monthId, dayNumber);
                    this.solarData = this.aggregationToDay(solarSource, monthId, dayNumber);
                    this.gasData = this.aggregationToDay(gasSource, monthId, dayNumber);

                    this.activeAggregation = "day";
                    this.activeDay = dayNumber;
                    this.updateSeries();
                }
                break;
        }
    }

    @HostListener('window:refreshData')
    refreshData() {
        this.zoomGraph(this.activeZoom);
    }

    updateSeries() {
        this.energySerieData = [
            {
                'data': this.solarData,
                'color': '#f2a200',
                'title': 'Energie productie'
            }, {
                'data': this.energyData,
                'color': '#a1c4d0',
                'title': 'Energie verbruik'
            }
        ];

        this.gasSerieData = [
            {
                'data': this.gasData,
                'color': '#a5acb0',
                'title': 'Gas verbruik'
            }
        ];

        if (this.energyData != null) {
            this.energyUsage = this.getSeriesTotal(this.energyData);
        }

        if (this.solarData != null) {
            this.energyReturn = this.getSeriesTotal(this.solarData);
        }

        if (this.energyData != null && this.solarData != null) {
            this.energyBalance = this.energyReturn - this.energyUsage;
        }
    }

    getMonthId(targetMonth: string): number {
        var result = -1;

        for (var i = 0; i < this.monthData.length; i++) {
            if (this.monthData[i] == targetMonth) {
                result = i;
                break;
            }
        }

        return result;
    }

    get canNext(): boolean {
        let zoomObject = this.activeZoom;
        switch (zoomObject.depth) {
            case 0: // Year
                return false;
            case 1: // Month
                return zoomObject.month != -1 && zoomObject.month < 11;
            case 2: // Day                
                // TODO: more advanced check
                return zoomObject.day != -1 && zoomObject.day < 31;
        }
        return false;
    }

    next() {
        let zoomObject = this.activeZoom;
        switch (zoomObject.depth) {
            case 1: // Month
                var monthId = zoomObject.month;
                zoomObject.month++;
                zoomObject.label = this.monthData[zoomObject.month];
                break;
            case 2: // Day                
                // TODO: more advanced check
                zoomObject.day++;
                zoomObject.label = "" + zoomObject.day;
                break;
        }
        this.zoomGraph(zoomObject);
    }


    get canPrevious(): boolean {
        let zoomObject = this.activeZoom;
        switch (zoomObject.depth) {
            case 0: // Year
                return false;
            case 1: // Month
                return zoomObject.month > 0;
            case 2: // Day                
                // TODO: more advanced check
                return zoomObject.day > 1;
        }
        return false;
    }

    previous() {
        let zoomObject = this.activeZoom;
        switch (zoomObject.depth) {
            case 1: // Month
                var monthId = zoomObject.month;
                zoomObject.month--;
                zoomObject.label = this.monthData[zoomObject.month];
                break;
            case 2: // Day                
                zoomObject.day--;
                zoomObject.label = "" + zoomObject.day;
                break;
        }
        this.zoomGraph(zoomObject);
    }
}



// WEBPACK FOOTER //
// ./ClientApp/app/pages/my-energy/my-energy.component.ts